export const isEmpty = (i) => i.end <= i.start;
export const durationMs = (i) => Math.max(0, i.end - i.start);
/** Half-open overlap: touching at an endpoint is *not* an overlap. */
export const overlaps = (a, b) => a.start < b.end && b.start < a.end;
export const contains = (outer, inner) => outer.start <= inner.start && inner.end <= outer.end;
const byStart = (a, b) => a.start - b.start || a.end - b.end;
/**
 * Sort, drop empties, and coalesce anything overlapping *or touching*.
 * Touching intervals are merged because `[9,10)` and `[10,11)` describe one
 * continuous stretch of open time, and slicing them separately would lose every
 * slot that straddles 10:00.
 */
export function merge(intervals) {
    const sorted = intervals.filter((i) => !isEmpty(i)).sort(byStart);
    const out = [];
    for (const i of sorted) {
        const last = out[out.length - 1];
        if (last !== undefined && i.start <= last.end) {
            if (i.end > last.end)
                last.end = i.end;
        }
        else {
            out.push({ start: i.start, end: i.end });
        }
    }
    return out;
}
/** `base` minus `cuts`. Both are normalized first; the result is normalized. */
export function subtract(base, cuts) {
    const holes = merge(cuts);
    const out = [];
    for (const b of merge(base)) {
        let cursor = b.start;
        for (const h of holes) {
            if (h.end <= cursor)
                continue; // hole is entirely behind us
            if (h.start >= b.end)
                break; // holes are sorted; the rest are past this base
            if (h.start > cursor)
                out.push({ start: cursor, end: Math.min(h.start, b.end) });
            cursor = Math.max(cursor, h.end);
            if (cursor >= b.end)
                break;
        }
        if (cursor < b.end)
            out.push({ start: cursor, end: b.end });
    }
    return out.filter((i) => !isEmpty(i));
}
/**
 * The times at which `units` or more of `intervals` overlap — a **pool's**
 * unavailable time (spec §4.6, restaurant feature 1).
 *
 * A restaurant's eight interchangeable 2-tops are one resource with `units = 8`,
 * and each seated party is one interval on one lane. The pool is only closed
 * when every lane is taken, so "unavailable" is not the union of the bookings —
 * it is the part of the timeline the union covers `units` deep.
 *
 * `units = 1` is exactly `merge`, and it is written as that rather than as a
 * special case of the sweep so the degenerate tenant (every spa) walks the same
 * code it always did.
 *
 * Half-open throughout: at an instant where one booking ends and another
 * begins, the depth does not momentarily rise. That is why ends are processed
 * before starts at equal timestamps, and it is the same reason `[9,10)` and
 * `[10,11)` do not collide anywhere else in this file.
 */
export function saturated(intervals, units) {
    if (units <= 1)
        return merge(intervals);
    const events = [];
    for (const i of intervals) {
        if (isEmpty(i))
            continue;
        events.push({ at: i.start, delta: 1 }, { at: i.end, delta: -1 });
    }
    events.sort((a, b) => a.at - b.at || a.delta - b.delta);
    const out = [];
    let depth = 0;
    let openedAt = 0;
    for (const event of events) {
        const before = depth;
        depth += event.delta;
        if (before < units && depth >= units)
            openedAt = event.at;
        else if (before >= units && depth < units)
            out.push({ start: openedAt, end: event.at });
    }
    return merge(out);
}
/**
 * The most `intervals` that cover any single instant **of `span`** — the
 * occupancy depth a candidate offer would have to fit alongside.
 *
 * `saturated` answers "where is the pool full"; this answers "how full is the
 * pool while *this* block runs", which is the question a pooled offer asks
 * (v1.8 §2: at every instant, offered depth plus busy depth never exceeds
 * `units`). Half-open throughout and for the same reason: a booking ending at
 * 19:30 and one starting at 19:30 are one deep, not two, so ends are processed
 * before starts at equal timestamps.
 *
 * Intervals are clipped to `span` first, so a booking that merely surrounds the
 * block counts once rather than widening the answer, and an empty `span`
 * answers `0`.
 */
export function maxDepthWithin(span, intervals) {
    const events = [];
    for (const i of intervals) {
        const start = Math.max(i.start, span.start);
        const end = Math.min(i.end, span.end);
        if (end <= start)
            continue;
        events.push({ at: start, delta: 1 }, { at: end, delta: -1 });
    }
    events.sort((a, b) => a.at - b.at || a.delta - b.delta);
    let depth = 0;
    let deepest = 0;
    for (const event of events) {
        depth += event.delta;
        if (depth > deepest)
            deepest = depth;
    }
    return deepest;
}
export function intersect(a, b) {
    const out = [];
    const bs = merge(b);
    for (const x of merge(a)) {
        for (const y of bs) {
            if (y.start >= x.end)
                break;
            const start = Math.max(x.start, y.start);
            const end = Math.min(x.end, y.end);
            if (end > start)
                out.push({ start, end });
        }
    }
    return out;
}
export function totalMs(intervals) {
    return merge(intervals).reduce((sum, i) => sum + durationMs(i), 0);
}
/**
 * Starts on `grid` (anchored at `anchor`) for blocks of `blockMs` inside
 * `free`, advancing per `advance` (above; `'block'` — packed — is the default
 * and is what every caller did before v1.8).
 *
 * In `'block'` mode any subset of what is emitted can be booked together
 * without a 409, because nothing overlaps. In `'grid'` mode that guarantee is
 * **not** this function's any more — it emits candidates, and the caller admits
 * them against the lanes actually free. Emitting them here rather than widening
 * the packer's step is deliberate: the grid is what a tenant configured, and a
 * pool's seating granularity should be that number rather than a function of
 * how long a table is held.
 *
 * `after` suppresses starts at or before it (a time already in the past). A
 * suppressed candidate advances by the *grid*, not by the block, so the first
 * offerable start still packs from the earliest feasible point rather than
 * from the shadow of a slot nobody could take.
 *
 * The grid is anchored at the day's opening rather than at the epoch, because a
 * timezone with a `:30` or `:45` offset would otherwise produce slots at
 * 08:15, 08:45 for a spa that opens at 08:00.
 */
export function gridStarts(free, blockMs, gridMs, anchor, after = Number.NEGATIVE_INFINITY, advance = 'block') {
    if (blockMs <= 0 || gridMs <= 0 || durationMs(free) < blockMs)
        return [];
    const alignUp = (t) => anchor + Math.ceil((t - anchor) / gridMs) * gridMs;
    const out = [];
    let t = alignUp(free.start);
    while (t + blockMs <= free.end) {
        if (t <= after) {
            t += gridMs;
            continue;
        }
        out.push(t);
        t = advance === 'grid' ? t + gridMs : alignUp(t + blockMs);
    }
    return out;
}
//# sourceMappingURL=interval.js.map